import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import knowledgeApi from '@/lib/knowledge-api'

export interface TestAssistantInfo {
  success: boolean
  test_assistant_id: string | null
  test_assistant_name: string | null
  created_at: number | null
  updated_at: number | null
  settings_synced: boolean
  test_settings: Record<string, any>
  has_test_assistant: boolean
  default_system_prompt: string
  assistant_prompt: string
}

export function useTestAssistantInfo(kbId: string) {
  return useQuery({
    queryKey: ['test-assistant-info', kbId],
    queryFn: async () => {
      const response = await knowledgeApi.get(`/knowledge/${kbId}/test-assistant/info/`)
      return response.data as TestAssistantInfo
    },
    enabled: !!kbId,
    refetchOnMount: true,
  })
}

export function useCreateTestAssistant(kbId: string, assistantId?: string) {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (settings?: Record<string, any>) => {
      const response = await knowledgeApi.post(`/knowledge/${kbId}/test-assistant/create/`, {
        settings: settings || {},
        assistant_id: assistantId  // Include assistant_id to get all KBs from the assistant
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-assistant-info', kbId] })
    },
  })
}

export function useUpdateTestAssistant(kbId: string) {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (settings: Record<string, any>) => {
      const response = await knowledgeApi.put(`/knowledge/${kbId}/test-assistant/update/`, {
        settings
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-assistant-info', kbId] })
    },
  })
}

export function useSaveTestSettingsToKB(kbId: string) {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async () => {
      const response = await knowledgeApi.post(`/knowledge/${kbId}/test-assistant/save-to-kb/`)
      return response.data
    },
    onSuccess: () => {
      // Invalidate KB settings as they've been updated
      queryClient.invalidateQueries({ queryKey: ['kb-settings', kbId] })
    },
  })
}

export function useDeleteTestAssistant(kbId: string) {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async () => {
      const response = await knowledgeApi.post(`/knowledge/${kbId}/test-assistant/delete/`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-assistant-info', kbId] })
    },
  })
}

export function useClearTestSession(kbId: string) {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async () => {
      const response = await knowledgeApi.post(`/knowledge/${kbId}/test-assistant/clear-session/`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['test-assistant-info', kbId] })
      queryClient.invalidateQueries({ queryKey: ['active-session-count', kbId] })
    },
  })
}

export function useActiveSessionCount(kbId: string) {
  return useQuery({
    queryKey: ['active-session-count', kbId],
    queryFn: async () => {
      const response = await knowledgeApi.get(`/knowledge/${kbId}/test-assistant/session-count/`)
      return response.data as { sro_sessions: number; sticky_sessions: number; total: number }
    },
    enabled: !!kbId,
    refetchInterval: 10000, // Poll every 10 seconds
  })
}
